import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { LanguageModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import { ProductQuantityBreakPricingStyles } from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CustomBreakPricing, { CustomBreakPricingStyles } from "@waytek/Overrides/Components/CustomBreakPricing";
import ProductLabels from "@waytek/Overrides/Components/ProductLabels";
import { isAuthenticated } from "@waytek/Utilities/global/users";
import { fontFamily } from "@waytek/Utilities/variables";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    product: ProductModel;
    widgetId: string;
    showBrand: boolean;
    showPartNumbers: boolean;
    showPrice: boolean;
    showAvailability: boolean;
    showAddToCart: boolean;
    showAddToList: boolean;
    extendedStyles?: PurchasedProductCardStyles;
    language: LanguageModel | null;
    currencySymbol: string | undefined;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productSettings: getSettingsCollection(state).productSettings,
    productInfo: getProductInfoFromList(state, ownProps.widgetId, ownProps.product.id),
    isAuthenticated: isAuthenticated(state),
});

const mapDispatchToProps = {
    updateProductInfo,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface PurchasedProductCardStyles {
    container?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    leftColumnGridContainer?: GridContainerProps;
    productImage?: ProductImageStyles;
    imageGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnGridContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    priceBreakGridItem?: GridItemProps;
    infoContainer?: GridContainerProps;
    brandStyles?: ProductBrandStyles;
    brandStylesMobile?: GridItemProps;
    descriptionStyles?: ProductDescriptionStyles;
    brandGridItem?: GridItemProps;
    brandMobileGridItem?: GridItemProps;
    descriptionGridItem?: GridItemProps;
    descriptionMobileGridItem?: GridItemProps;
    partNumbersGridItemMobile?: GridItemProps;
    partNumbersGridItem?: GridItemProps;
    partNumbersStyles?: ProductPartNumbersStyles;
    productLabelsGridItem?: GridItemProps;
    productLabelsGridContainer?: GridContainerProps;
    productMinQtyWrapper?: GridContainerProps;
    productStockWrapper?: GridContainerProps;
    productMinQtyMobile?: GridItemProps;
    productMinQtyStockItem?: GridItemProps;
    productMinQtyStockLabel?: TypographyPresentationProps;
    productMinQtyStockValue?: TypographyPresentationProps;
    availabilityGridItem?: GridItemProps;
    availabilityStyles?: ProductAvailabilityStyles;
    priceGridItem?: GridItemProps;
    priceStyles?: ProductPriceStyles;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    actionsGridItem?: GridItemProps;
    actionsContainer?: GridContainerProps;
    quantityGridItem?: GridItemProps;
    quantityOrdered?: TextFieldPresentationProps;
    addToGridItem?: GridItemProps;
    addToCartButton?: ButtonPresentationProps;
    addToListWrapper?: InjectableCss;
    addToListLink?: ProductAddToListLinkStyles;
    customBreakPricingStyles?: CustomBreakPricingStyles;

    /* Verndale Custom Feature - New Styles */
    headingAndTextStyles?: SmallHeadingAndTextStyles;
}

export const purchasedProductCardStyles: PurchasedProductCardStyles = {
    container: {
        gap: 0,
        css: css`
            padding: 10px 0;
            border-bottom: 1px solid ${getColor("common.border")};
        `,
    },
    leftColumnGridItem: {
        width: [4, 4, 2, 1, 1],
        css: css`
            padding-right: 15px;
        `,
    },
    rightColumnGridItem: {
        width: [8, 8, 10, 11, 11],
    },
    rightColumnGridContainer: { gap: 0 },
    infoGridItem: {
        width: [12, 12, 5, 6, 6],
    },
    infoContainer: {
        gap: 0,
        css: css`
            width: 100%;
        `,
    },
    descriptionStyles: {
        productDetailLink: {
            css: css`
                width: 100%;
            `,
            typographyProps: {
                size: 14,
            },
        },
    },
    brandGridItem: {
        width: 12,
    },
    descriptionGridItem: {
        width: 12,
    },
    partNumbersGridItem: {
        width: 12,
    },
    partNumbersStyles: {
        erpNumberLabelText: {
            weight: "normal",
            color: "text.disabled",
        },
        erpNumberValueText: {
            color: "text.disabled",
            css: css`
                padding-left: 5px;
            `,
        },
    },
    availabilityGridItem: {
        width: 12,
    },
    priceGridItem: {
        width: [12, 12, 3, 3, 3],
        css: css`
            flex-direction: column;
            padding-right: 20px;
        `,
    },
    priceStyles: {
        wrapper: {
            css: css`
                display: flex;
                flex-direction: column;
                width: auto;
                text-align: end;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            // eslint-disable-next-line
                            /* eslint-disable sonarjs/no-nested-template-literals */
                            css`
                                align-self: flex-end;
                            `,
                            // eslint-disable-next-line
                            /* eslint-enable sonarjs/no-nested-template-literals */
                        ],
                        "min",
                    )}
            `,
        },
        price: {
            priceText: {
                fontFamily,
                weight: 500,
                size: "21px",
            },
            unitOfMeasureText: {
                fontFamily,
                weight: "normal",
            },
        },
        quoteMessage: {
            text: {
                fontFamily,
            },
        },
    },
    actionsGridItem: {
        width: [12, 12, 4, 3, 3],
    },
    actionsContainer: {
        gap: 0,
        css: css`
            width: 100%;
        `,
    },
    quantityGridItem: { width: 3 },
    addToGridItem: {
        width: 9,
        css: css`
            flex-direction: column;
            align-items: center;
            padding-left: 10px;
        `,
    },
    addToCartButton: {
        css: css`
            width: 100%;
        `,
    },
    addToListWrapper: {
        css: css`
            margin-top: 5px;
        `,
    },
    addToListLink: {
        link: {
            typographyProps: { size: 13 },
        },
    },
};

const PurchasedProductCard: React.FC<Props> = ({
    product,
    productInfo,
    showBrand,
    showPartNumbers,
    showPrice,
    showAvailability,
    showAddToCart,
    showAddToList,
    productSettings,
    extendedStyles,
    updateProductInfo,
    isAuthenticated,
    widgetId,
    language,
    currencySymbol,
}) => {
    const [styles] = React.useState(() => mergeToNew(purchasedProductCardStyles, extendedStyles));

    if (!productInfo || !product) {
        return null;
    }

    const productContext: ProductContextModel = {
        product,
        productInfo,
        onUnitOfMeasureChanged: unitOfMeasure => {
            updateProductInfo({ id: widgetId, productId: product.id, unitOfMeasure });
        },
        onQtyOrderedChanged: qtyOrdered => {
            updateProductInfo({ id: widgetId, productId: product.id, qtyOrdered });
        },
    };

    return (
        <ProductContext.Provider value={productContext} data-test-selector="widget_PurchasedProducts">
            <GridContainer {...styles.container} data-test-selector={`purchasedProductCard_${product.id}`}>
                <GridItem {...styles.leftColumnGridItem}>
                    <GridContainer {...styles.leftColumnGridContainer}>
                        <GridItem {...styles.imageGridItem}>
                            <ProductImage
                                extendedStyles={styles.productImage}
                                product={productContext}
                                showSmallImage={true}
                            />
                        </GridItem>
                        <GridItem {...styles.descriptionMobileGridItem}>
                            {product.brand && showBrand && (
                                <ProductBrand brand={product.brand} {...styles.brandStylesMobile} />
                            )}

                            {showPartNumbers && (
                                <GridContainer {...styles.partNumbersGridItemMobile}>
                                    <ProductPartNumbers
                                        productNumber={product.productNumber}
                                        customerProductNumber={product.customerProductNumber}
                                        manufacturerItem={product.manufacturerItem}
                                        extendedStyles={styles.partNumbersStyles}
                                    />
                                </GridContainer>
                            )}
                            <GridContainer {...styles.productLabelsGridContainer}>
                                <GridItem width={12}>
                                    <ProductLabels
                                        size={24}
                                        gap={10}
                                        forceAlignment={true}
                                        properties={product.properties}
                                    />
                                </GridItem>
                            </GridContainer>
                        </GridItem>
                    </GridContainer>
                </GridItem>
                <GridItem {...styles.rightColumnGridItem}>
                    <GridContainer {...styles.rightColumnGridContainer}>
                        <GridItem {...styles.infoGridItem}>
                            <GridContainer {...styles.infoContainer}>
                                {product.brand && showBrand && (
                                    <GridItem {...styles.brandGridItem}>
                                        <ProductBrand brand={product.brand} extendedStyles={styles.brandStyles} />
                                    </GridItem>
                                )}
                                <GridItem {...styles.descriptionGridItem}>
                                    <ProductDescription
                                        product={productContext}
                                        extendedStyles={styles.descriptionStyles}
                                    />
                                </GridItem>
                                {showPartNumbers && (
                                    <GridItem {...styles.partNumbersGridItem}>
                                        <ProductPartNumbers
                                            productNumber={product.productNumber}
                                            customerProductNumber={product.customerProductNumber}
                                            manufacturerItem={product.manufacturerItem}
                                            extendedStyles={styles.partNumbersStyles}
                                        />
                                    </GridItem>
                                )}

                                <GridItem width={12} {...styles.productLabelsGridItem}>
                                    <ProductLabels
                                        size={24}
                                        gap={10}
                                        forceAlignment={true}
                                        properties={product.properties}
                                    />
                                </GridItem>

                                <GridContainer className="desktop" {...styles.productMinQtyWrapper} gap={0}>
                                    <GridItem width={12} {...styles.productMinQtyStockItem}>
                                        <Typography {...styles.productMinQtyStockLabel}>
                                            {translate("Min. Order Qty:")}
                                        </Typography>
                                        <Typography {...styles.productMinQtyStockValue}>
                                            {product.minimumOrderQty || 1}
                                        </Typography>
                                    </GridItem>
                                </GridContainer>

                                <GridContainer className="desktop" {...styles.productStockWrapper} gap={0}>
                                    <GridItem width={12} {...styles.productMinQtyStockItem}>
                                        <Typography {...styles.productMinQtyStockValue}>
                                            {productInfo.inventory?.qtyOnHand || 0} {translate("in Stock")}
                                        </Typography>
                                    </GridItem>
                                </GridContainer>

                                {productSettings.showInventoryAvailability && showAvailability && (
                                    <GridItem {...styles.availabilityGridItem}>
                                        <ProductContextAvailability extendedStyles={styles.availabilityStyles} />
                                    </GridItem>
                                )}
                            </GridContainer>
                        </GridItem>
                        <GridItem {...styles.priceBreakGridItem}>
                            <StyledWrapper>
                                <CustomBreakPricing
                                    language={language}
                                    currencySymbol={currencySymbol}
                                    extendedStyles={styles.customBreakPricingStyles}
                                    maxRows={3}
                                />

                                {product.properties["lastOrderNumber"] && (
                                    <SmallHeadingAndText
                                        heading={`${translate("Last Order Number")}: `}
                                        text={product.properties["lastOrderNumber"]}
                                        extendedStyles={styles.headingAndTextStyles}
                                        data-test-selector="lastOrderNumber"
                                    ></SmallHeadingAndText>
                                )}

                                {product.properties["lastPoNumber"] && (
                                    <SmallHeadingAndText
                                        heading={`${translate("Last PO Number")}: `}
                                        text={product.properties["lastPoNumber"]}
                                        extendedStyles={styles.headingAndTextStyles}
                                        data-test-selector="lastPONumber"
                                    ></SmallHeadingAndText>
                                )}

                                {product.properties["lastOrderDate"] && (
                                    <SmallHeadingAndText
                                        heading={`${translate("Last Order Date")}: `}
                                        text={getLocalizedDateTime({
                                            dateTime: new Date(product.properties["lastOrderDate"]),
                                            language,
                                        })}
                                        extendedStyles={styles.headingAndTextStyles}
                                        data-test-selector="lastOrderDate"
                                    ></SmallHeadingAndText>
                                )}
                            </StyledWrapper>
                        </GridItem>
                        <GridItem {...styles.productMinQtyMobile} width={12}>
                            <GridContainer className="mobile" {...styles.productMinQtyWrapper} gap={0}>
                                <GridItem width={12} {...styles.productMinQtyStockItem}>
                                    <Typography {...styles.productMinQtyStockLabel}>
                                        {translate("Min. Order Qty:")}
                                    </Typography>
                                    <Typography {...styles.productMinQtyStockValue}>
                                        {product.minimumOrderQty || 1}
                                    </Typography>
                                </GridItem>
                            </GridContainer>
                        </GridItem>
                        <GridItem {...styles.productMinQtyMobile} width={12}>
                            <GridContainer className="mobile" {...styles.productStockWrapper} gap={0}>
                                <GridItem {...styles.productMinQtyStockItem} width={12}>
                                    <Typography {...styles.productMinQtyStockValue}>
                                        {productInfo.inventory?.qtyOnHand || 0} {translate("in Stock")}
                                    </Typography>
                                </GridItem>
                            </GridContainer>
                        </GridItem>
                        <GridItem {...styles.actionsGridItem}>
                            <GridContainer {...styles.actionsContainer}>
                                <GridItem {...styles.priceGridItem}>
                                    {showPrice && (
                                        <>
                                            <ProductPrice
                                                product={productContext}
                                                showLabel={false}
                                                showSavings={true}
                                                showSavingsAmount={productSettings.showSavingsAmount}
                                                showSavingsPercent={productSettings.showSavingsPercent}
                                                extendedStyles={styles.priceStyles}
                                            />
                                            {/* <ProductQuantityBreakPricing extendedStyles={styles.quantityBreakPricing} />
                                    {product.unitOfMeasures && (
                                        <ProductUnitOfMeasureSelect labelOverride="" id={`${product.id}-uom`} />
                                            )} */}
                                        </>
                                    )}
                                </GridItem>
                                <GridItem {...styles.quantityGridItem}>
                                    {showAddToCart && (
                                        <ProductQuantityOrdered
                                            labelOverride=""
                                            extendedStyles={styles.quantityOrdered}
                                        />
                                    )}
                                </GridItem>
                                <GridItem {...styles.addToGridItem}>
                                    {showAddToCart && (
                                        <ProductAddToCartButton
                                            data-test-selector={`actionsAddToCart${product.id}`}
                                            extendedStyles={styles.addToCartButton}
                                        />
                                    )}
                                    {showAddToList && (
                                        <StyledWrapper {...styles.addToListWrapper}>
                                            <ProductAddToListLink
                                                data-test-selector={`actionsAddToList${product.id}`}
                                                extendedStyles={styles.addToListLink}
                                            />
                                        </StyledWrapper>
                                    )}
                                </GridItem>
                            </GridContainer>
                        </GridItem>
                    </GridContainer>
                </GridItem>
            </GridContainer>
        </ProductContext.Provider>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(PurchasedProductCard as any);
